import logger from '../utils/logger';
import React, { useEffect, useState } from 'react';
import '../style/PDFResultsModal.css';

const PDFResultsViewer = ({ orderData, orderType, patientId, onClose }) => {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const BASE_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchPDF = async () => {
      if (!orderData || !orderType || !patientId) {
        setError('Missing required information');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        // ALWAYS prefer the exact GCS object path recorded on the order. Generated
        // reports live at paths the static convention below cannot reproduce:
        //   lab       -> lab_orders/results/order_<id>_<timestamp>.pdf
        //                (lab_orders.results_pdf_path — analyzer auto-report and the
        //                 typed-results template both write a timestamped name)
        //   radiology -> reports/order_<id>_report.pdf
        //                (radiology_orders.report_pdf_path — AdvaPACS report consumer)
        // Using the stored path avoids the NoSuchKey error you get from guessing.
        //
        // The static naming convention is kept ONLY as a fallback for legacy orders
        // resulted by the manual PDF upload flow, which leaves the path column NULL.
        const storedPath =
          orderType === 'lab' ? orderData.results_pdf_path : orderData.report_pdf_path;

        let pdfName;

        if (storedPath) {
          pdfName = storedPath;
        } else if (orderType === 'lab') {
          // Legacy manual-upload naming convention
          // Example: Results/HealthHub_${orderId}_${patientId}_lab.pdf
          pdfName = `Results/HealthHub_${orderData.id}_${patientId}_lab.pdf`;
        } else if (orderType === 'radiology') {
          // Legacy manual-upload naming convention
          // Example: Results/HealthGuardian_${orderId}_${patientId}_radiology.pdf
          pdfName = `Results/HealthGuardian_${orderData.id}_${patientId}_radiology.pdf`;
        }

        logger.log('Fetching PDF:', pdfName);

        const res = await fetch(
          `${BASE_URL}/upload/generate-signed-url?pdfName=${encodeURIComponent(pdfName)}`
        );

        if (!res.ok) {
          throw new Error('Failed to fetch PDF');
        }

        const data = await res.json();
        setPdfUrl(data.signedUrl);
        setIsLoading(false);
      } catch (error) {
        logger.error('Error fetching PDF:', error);
        setError('Failed to load PDF. The document may not be available.');
        setIsLoading(false);
      }
    };

    fetchPDF();
  }, [orderData, orderType, patientId]);

  if (!orderData) return null;

  return (
    <div className="pdfViewer-modal-overlay open">
      <div className="pdfViewer-modal-content">
        <div className="pdfViewer-modal-header">
          <h3>
            {orderType === 'lab' ? 'Lab' : 'Radiology'} Results - Order #{orderData.id}
          </h3>
          <p className="pdfViewer-modal-subtitle">
            {orderData.exam_ordered}
          </p>
        </div>
        <div
          style={{
            width: '100%',
            height: '70vh',
            overflowY: 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {isLoading ? (
            <p>Loading PDF...</p>
          ) : error ? (
            <div style={{ textAlign: 'center', color: '#dc2626' }}>
              <p>{error}</p>
              <p style={{ fontSize: '14px', marginTop: '8px' }}>
                Please ensure the results have been uploaded.
              </p>
            </div>
          ) : pdfUrl ? (
            <iframe
              src={pdfUrl}
              title="PDF Viewer"
              width="100%"
              height="100%"
              className="pdfViewer-modal-element"
              style={{ border: 'none' }}
            />
          ) : null}
        </div>
        <div className="pdfViewer-modal-footer">
          <button className="pdfViewer-close-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PDFResultsViewer;
